import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/ui/Toast";
import {
  Button,
  Card,
  DataTable,
  Modal,
  Input,
  Select,
} from "../../components/ui";
import {
  TemplateEditor,
  DestinatariBuilder,
  RegolaConfigurator,
  EVENTI_TRIGGER,
} from "../../components/notifiche";
import api from "../../services/api";

// Stati email per badge
const STATI_EMAIL = {
  in_coda: {
    label: "In coda",
    color: "bg-yellow-100 text-yellow-700",
    icon: "⏳",
  },
  inviato: {
    label: "Inviato",
    color: "bg-green-100 text-green-700",
    icon: "✓",
  },
  errore: { label: "Errore", color: "bg-red-100 text-red-700", icon: "✗" },
  annullato: {
    label: "Annullato",
    color: "bg-gray-100 text-gray-700",
    icon: "⊘",
  },
};

function NotifichePage() {
  const navigate = useNavigate();
  const { apiRequest, user } = useAuth();
  const { showToast } = useToast();

  // Tab attiva
  const [activeTab, setActiveTab] = useState("dashboard");

  // Dati
  const [stats, setStats] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [regole, setRegole] = useState([]);
  const [storico, setStorico] = useState([]);
  const [tags, setTags] = useState({});

  // Loading states
  const [loading, setLoading] = useState({});

  // Modal states
  const [templateModal, setTemplateModal] = useState({
    open: false,
    mode: "create",
    data: null,
  });
  const [regolaModal, setRegolaModal] = useState({
    open: false,
    mode: "create",
    data: null,
  });
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    type: null,
    id: null,
    nome: "",
  });

  // Form states
  const [templateForm, setTemplateForm] = useState({
    nome: "",
    codice: "",
    oggetto: "",
    corpo: "",
    tipologia: "scadenza",
    allegati: [],
    attivo: true,
  });
  const [regolaForm, setRegolaForm] = useState({});

  // Allegati
  const [allegatiDisponibili, setAllegatiDisponibili] = useState([]);
  const [pdfPreviewFile, setPdfPreviewFile] = useState(null);

  // Filtri storico
  const [filtriStorico, setFiltriStorico] = useState({
    stato: "",
    dataInizio: "",
    dataFine: "",
  });

  // Carica dati iniziali
  useEffect(() => {
    loadDashboardData();
    loadTags();
  }, []);

  // Carica dati per tab
  useEffect(() => {
    if (activeTab === "templates") loadTemplates();
    if (activeTab === "regole") loadRegole();
    if (activeTab === "storico") loadStorico();
  }, [activeTab]);

  const loadDashboardData = async () => {
    setLoading((prev) => ({ ...prev, dashboard: true }));
    try {
      const [statsData, templatesData, regoleData, storicoData] =
        await Promise.all([
          api.get("/api/notifiche/stats"),
          api.get("/api/notifiche/templates?limit=5"),
          api.get("/api/notifiche/regole?limit=5"),
          api.get("/api/notifiche/storico?limit=10"),
        ]);
      setStats(statsData);
      setTemplates(templatesData.value || []);
      setRegole(regoleData.value || []);
      setStorico(storicoData.value || []);
    } catch (error) {
      console.error("Errore caricamento dashboard:", error);
      showToast("Errore caricamento dati", "error");
    } finally {
      setLoading((prev) => ({ ...prev, dashboard: false }));
    }
  };

  const loadTemplates = async () => {
    setLoading((prev) => ({ ...prev, templates: true }));
    try {
      const response = await api.get("/api/notifiche/templates");
      setTemplates(response.value || []);
    } catch (error) {
      console.error("Errore caricamento templates:", error);
    } finally {
      setLoading((prev) => ({ ...prev, templates: false }));
    }
  };

  const loadRegole = async () => {
    setLoading((prev) => ({ ...prev, regole: true }));
    try {
      const [regoleData, templatesData] = await Promise.all([
        api.get("/api/notifiche/regole"),
        api.get("/api/notifiche/templates"),
      ]);
      setRegole(regoleData.value || []);
      setTemplates(templatesData.value || []);
    } catch (error) {
      console.error("Errore caricamento regole:", error);
    } finally {
      setLoading((prev) => ({ ...prev, regole: false }));
    }
  };

  const loadStorico = async () => {
    setLoading((prev) => ({ ...prev, storico: true }));
    try {
      const params = new URLSearchParams();
      if (filtriStorico.stato) params.append("stato", filtriStorico.stato);
      if (filtriStorico.dataInizio)
        params.append("dataInizio", filtriStorico.dataInizio);
      if (filtriStorico.dataFine)
        params.append("dataFine", filtriStorico.dataFine);

      const response = await api.get(
        `/api/notifiche/storico?${params.toString()}`,
      );
      setStorico(response.value || []);
    } catch (error) {
      console.error("Errore caricamento storico:", error);
    } finally {
      setLoading((prev) => ({ ...prev, storico: false }));
    }
  };

  const loadTags = async () => {
    try {
      const response = await api.get("/api/notifiche/tags");
      setTags(response || {});
    } catch (error) {
      console.error("Errore caricamento tags:", error);
    }
  };

  const loadAllegatiDisponibili = async () => {
    try {
      const response = await api.get("/api/notifiche/allegati-disponibili");
      setAllegatiDisponibili(response.value || []);
    } catch (error) {
      console.error("Errore caricamento allegati:", error);
    }
  };

  // Handlers Template
  const openTemplateModal = (mode, template = null) => {
    if (mode === "edit" && template) {
      setTemplateForm({
        nome: template.nome || "",
        codice: template.codice || "",
        oggetto: template.oggetto || "",
        corpo: template.corpo || "",
        tipologia: template.tipologia || "scadenza",
        allegati: template.allegati || [],
        attivo: template.attivo !== false,
      });
    } else {
      setTemplateForm({
        nome: "",
        codice: "",
        oggetto: "",
        corpo: "",
        tipologia: "scadenza",
        allegati: [],
        attivo: true,
      });
    }
    loadAllegatiDisponibili();
    setTemplateModal({ open: true, mode, data: template });
  };

  const saveTemplate = async () => {
    try {
      if (templateModal.mode === "create") {
        await api.post("/api/notifiche/templates", templateForm);
        showToast("Template creato con successo", "success");
      } else {
        await api.put(
          `/api/notifiche/templates/${templateModal.data.id}`,
          templateForm,
        );
        showToast("Template aggiornato con successo", "success");
      }
      setTemplateModal({ open: false, mode: "create", data: null });
      loadTemplates();
    } catch (error) {
      showToast(
        error.response?.data?.error || "Errore salvataggio template",
        "error",
      );
    }
  };

  // Handlers Regola
  const openRegolaModal = (mode, regola = null) => {
    if (mode === "edit" && regola) {
      setRegolaForm({
        nome: regola.nome || "",
        templateId: regola.templateId || "",
        evento: regola.evento || "",
        destinatari: regola.destinatari || {
          tipo: "",
          ruoli: [],
          emailCustom: [],
          cc: [],
        },
        oraInvio: regola.oraInvio || "09:00",
        attivo: regola.attivo !== false,
        priorita: regola.priorita || 100,
      });
    } else {
      setRegolaForm({
        nome: "",
        templateId: "",
        evento: "",
        destinatari: { tipo: "", ruoli: [], emailCustom: [], cc: [] },
        oraInvio: "09:00",
        attivo: true,
        priorita: 100,
      });
    }
    setRegolaModal({ open: true, mode, data: regola });
  };

  const saveRegola = async () => {
    try {
      if (regolaModal.mode === "create") {
        await api.post("/api/notifiche/regole", regolaForm);
        showToast("Regola creata con successo", "success");
      } else {
        await api.put(
          `/api/notifiche/regole/${regolaModal.data.id}`,
          regolaForm,
        );
        showToast("Regola aggiornata con successo", "success");
      }
      setRegolaModal({ open: false, mode: "create", data: null });
      loadRegole();
    } catch (error) {
      showToast(
        error.response?.data?.error || "Errore salvataggio regola",
        "error",
      );
    }
  };

  // Handler Delete
  const confirmDelete = async () => {
    try {
      if (deleteModal.type === "template") {
        await api.delete(`/api/notifiche/templates/${deleteModal.id}`);
        showToast("Template eliminato", "success");
        loadTemplates();
      } else if (deleteModal.type === "regola") {
        await api.delete(`/api/notifiche/regole/${deleteModal.id}`);
        showToast("Regola eliminata", "success");
        loadRegole();
      }
      setDeleteModal({ open: false, type: null, id: null, nome: "" });
    } catch (error) {
      showToast(error.response?.data?.error || "Errore eliminazione", "error");
    }
  };

  // Colonne tabelle
  const templatesColumns = [
    { key: "nome", label: "Nome", sortable: true },
    {
      key: "codice",
      label: "Codice",
      render: (row) => (
        <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">
          {row.codice}
        </code>
      ),
    },
    {
      key: "tipologia",
      label: "Tipologia",
      render: (row) => <span className="capitalize">{row.tipologia}</span>,
    },
    {
      key: "oggetto",
      label: "Oggetto",
      render: (row) => (
        <div
          className="truncate max-w-xs text-sm text-gray-600"
          title={row.oggetto}
        >
          {row.oggetto}
        </div>
      ),
    },
    {
      key: "attivo",
      label: "Stato",
      render: (row) => (
        <span
          className={`px-2 py-0.5 text-xs font-medium rounded-full ${
            row.attivo
              ? "bg-green-100 text-green-700"
              : "bg-gray-100 text-gray-500"
          }`}
        >
          {row.attivo ? "Attivo" : "Disattivato"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "",
      render: (row) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => openTemplateModal("edit", row)}
          >
            Modifica
          </Button>
          {!row.default && (
            <Button
              size="sm"
              variant="ghost"
              className="text-red-600"
              onClick={() =>
                setDeleteModal({
                  open: true,
                  type: "template",
                  id: row.id,
                  nome: row.nome,
                })
              }
            >
              Elimina
            </Button>
          )}
        </div>
      ),
    },
  ];

  const regoleColumns = [
    { key: "nome", label: "Nome Regola", sortable: true },
    {
      key: "evento",
      label: "Trigger",
      render: (row) => {
        const evento = EVENTI_TRIGGER.flatMap((c) => c.events).find(
          (e) => e.value === row.evento,
        );
        return <span className="text-sm">{evento?.label || row.evento}</span>;
      },
    },
    {
      key: "template",
      label: "Template",
      render: (row) => {
        const template = templates.find((t) => t.id === row.templateId);
        return <span className="text-sm">{template?.nome || "-"}</span>;
      },
    },
    { key: "oraInvio", label: "Ora Invio" },
    {
      key: "attivo",
      label: "Stato",
      render: (row) => (
        <span
          className={`px-2 py-0.5 text-xs font-medium rounded-full ${
            row.attivo
              ? "bg-green-100 text-green-700"
              : "bg-gray-100 text-gray-500"
          }`}
        >
          {row.attivo ? "Attiva" : "Disattivata"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "",
      render: (row) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => openRegolaModal("edit", row)}
          >
            Modifica
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-red-600"
            onClick={() =>
              setDeleteModal({
                open: true,
                type: "regola",
                id: row.id,
                nome: row.nome,
              })
            }
          >
            Elimina
          </Button>
        </div>
      ),
    },
  ];

  const storicoColumns = [
    {
      key: "dataInvio",
      label: "Data/Ora",
      sortable: true,
      render: (row) => new Date(row.dataInvio).toLocaleString("it-IT"),
    },
    {
      key: "destinatario",
      label: "Destinatario",
      render: (row) => (
        <div>
          <div className="font-medium text-sm">
            {row.destinatarioNome || row.destinatario}
          </div>
          {row.destinatarioNome && (
            <div className="text-xs text-gray-500">{row.destinatario}</div>
          )}
        </div>
      ),
    },
    {
      key: "oggetto",
      label: "Oggetto",
      render: (row) => (
        <div className="truncate max-w-xs text-sm" title={row.oggetto}>
          {row.oggetto}
        </div>
      ),
    },
    {
      key: "stato",
      label: "Stato",
      render: (row) => {
        const stato = STATI_EMAIL[row.stato] || STATI_EMAIL.in_coda;
        return (
          <span
            className={`px-2 py-0.5 text-xs font-medium rounded-full ${stato.color}`}
          >
            {stato.icon} {stato.label}
          </span>
        );
      },
    },
    {
      key: "inviatoDa",
      label: "Inviato da",
      render: (row) => row.inviatoDa || "Sistema",
    },
  ];

  // Tabs config
  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: "📊" },
    { id: "templates", label: "Template", icon: "📝" },
    { id: "regole", label: "Regole", icon: "⚙️" },
    { id: "storico", label: "Storico", icon: "📜" },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <a href="/admin" className="hover:text-primary-600">
            Admin
          </a>
          <span>/</span>
          <span>Notifiche</span>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Sistema Notifiche Email
            </h1>
            <p className="text-gray-500 mt-1">
              Gestisci template, regole automatiche e invia notifiche manuali
            </p>
          </div>
          <Button
            variant="primary"
            onClick={() => navigate("/admin/notifiche/invia")}
          >
            + Invia Notifica
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-3 font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === tab.id
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <span>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* TAB: Dashboard */}
      {activeTab === "dashboard" && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="text-sm text-gray-500">Email Inviate (oggi)</div>
              <div className="text-3xl font-bold text-gray-900 mt-1">
                {stats?.inviati || 0}
              </div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-gray-500">Inviate (settimana)</div>
              <div className="text-3xl font-bold text-gray-900 mt-1">
                {stats?.totale || 0}
              </div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-gray-500">Errori (settimana)</div>
              <div className="text-3xl font-bold text-red-600 mt-1">
                {stats?.errori || 0}
              </div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-gray-500">Regole Attive</div>
              <div className="text-3xl font-bold text-green-600 mt-1">
                {regole.filter((r) => r.attivo).length || 0}
              </div>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Azioni Rapide
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Button
                variant="secondary"
                onClick={() => navigate("/admin/notifiche/invia")}
              >
                📧 Nuova Email
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setActiveTab("templates");
                  openTemplateModal("create");
                }}
              >
                📝 Nuovo Template
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setActiveTab("regole");
                  openRegolaModal("create");
                }}
              >
                ⚙️ Nuova Regola
              </Button>
              <Button
                variant="secondary"
                onClick={() => setActiveTab("storico")}
              >
                📜 Vedi Storico
              </Button>
            </div>
          </Card>

          {/* Recent Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Ultime Email
                </h3>
                <button
                  onClick={() => setActiveTab("storico")}
                  className="text-sm text-primary-600 hover:underline"
                >
                  Vedi tutto
                </button>
              </div>
              <div className="space-y-3">
                {storico.slice(0, 5).map((email, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between py-2 border-b last:border-0"
                  >
                    <div>
                      <div className="font-medium text-sm">
                        {email.destinatarioNome || email.destinatario}
                      </div>
                      <div className="text-xs text-gray-500 truncate max-w-xs">
                        {email.oggetto}
                      </div>
                    </div>
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        STATI_EMAIL[email.stato]?.color || "bg-gray-100"
                      }`}
                    >
                      {STATI_EMAIL[email.stato]?.label || email.stato}
                    </span>
                  </div>
                ))}
                {storico.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">
                    Nessuna email inviata
                  </p>
                )}
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Regole Attive
                </h3>
                <button
                  onClick={() => setActiveTab("regole")}
                  className="text-sm text-primary-600 hover:underline"
                >
                  Gestisci
                </button>
              </div>
              <div className="space-y-3">
                {regole
                  .filter((r) => r.attivo)
                  .slice(0, 5)
                  .map((regola, idx) => {
                    const evento = EVENTI_TRIGGER.flatMap((c) => c.events).find(
                      (e) => e.value === regola.evento,
                    );
                    return (
                      <div
                        key={idx}
                        className="flex items-center justify-between py-2 border-b last:border-0"
                      >
                        <div>
                          <div className="font-medium text-sm">
                            {regola.nome}
                          </div>
                          <div className="text-xs text-gray-500">
                            {evento?.label || regola.evento}
                          </div>
                        </div>
                        <span className="text-xs text-gray-500">
                          {regola.oraInvio}
                        </span>
                      </div>
                    );
                  })}
                {regole.filter((r) => r.attivo).length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">
                    Nessuna regola attiva
                  </p>
                )}
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* TAB: Templates */}
      {activeTab === "templates" && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Template Email</h2>
            <Button
              variant="primary"
              onClick={() => openTemplateModal("create")}
            >
              + Nuovo Template
            </Button>
          </div>
          <Card className="p-0 overflow-hidden">
            <DataTable
              data={templates}
              columns={templatesColumns}
              loading={loading.templates}
              searchable
              searchPlaceholder="Cerca template..."
              emptyMessage="Nessun template configurato"
            />
          </Card>
        </div>
      )}

      {/* TAB: Regole */}
      {activeTab === "regole" && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Regole Invio Automatico</h2>
            <Button variant="primary" onClick={() => openRegolaModal("create")}>
              + Nuova Regola
            </Button>
          </div>
          <Card className="p-0 overflow-hidden">
            <DataTable
              data={regole}
              columns={regoleColumns}
              loading={loading.regole}
              searchable
              searchPlaceholder="Cerca regola..."
              emptyMessage="Nessuna regola configurata"
            />
          </Card>
        </div>
      )}

      {/* TAB: Storico */}
      {activeTab === "storico" && (
        <div>
          {/* Filtri */}
          <Card className="p-4 mb-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[150px]">
                <Select
                  label="Stato"
                  value={filtriStorico.stato}
                  onChange={(e) =>
                    setFiltriStorico((prev) => ({
                      ...prev,
                      stato: e.target.value,
                    }))
                  }
                  options={[
                    { value: "", label: "Tutti gli stati" },
                    { value: "inviato", label: "Inviato" },
                    { value: "errore", label: "Errore" },
                    { value: "in_coda", label: "In coda" },
                  ]}
                />
              </div>
              <div className="flex-1 min-w-[150px]">
                <Input
                  label="Data inizio"
                  type="date"
                  value={filtriStorico.dataInizio}
                  onChange={(e) =>
                    setFiltriStorico((prev) => ({
                      ...prev,
                      dataInizio: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="flex-1 min-w-[150px]">
                <Input
                  label="Data fine"
                  type="date"
                  value={filtriStorico.dataFine}
                  onChange={(e) =>
                    setFiltriStorico((prev) => ({
                      ...prev,
                      dataFine: e.target.value,
                    }))
                  }
                />
              </div>
              <Button variant="secondary" onClick={loadStorico}>
                Filtra
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setFiltriStorico({ stato: "", dataInizio: "", dataFine: "" });
                  loadStorico();
                }}
              >
                Reset
              </Button>
            </div>
          </Card>

          <Card className="p-0 overflow-hidden">
            <DataTable
              data={storico}
              columns={storicoColumns}
              loading={loading.storico}
              searchable
              searchPlaceholder="Cerca nello storico..."
              emptyMessage="Nessuna email nello storico"
            />
          </Card>
        </div>
      )}

      {/* Modal Template */}
      <Modal
        isOpen={templateModal.open}
        onClose={() =>
          setTemplateModal({ open: false, mode: "create", data: null })
        }
        title={
          templateModal.mode === "create"
            ? "Nuovo Template"
            : "Modifica Template"
        }
        size="xl"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Nome Template *"
              value={templateForm.nome}
              onChange={(e) =>
                setTemplateForm((prev) => ({ ...prev, nome: e.target.value }))
              }
              placeholder="Es: Reminder 30 giorni"
            />
            <Input
              label="Codice *"
              value={templateForm.codice}
              onChange={(e) =>
                setTemplateForm((prev) => ({
                  ...prev,
                  codice: e.target.value.toLowerCase().replace(/\s+/g, "_"),
                }))
              }
              placeholder="Es: reminder_30"
              helperText="Identificativo univoco (senza spazi)"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Tipologia"
              value={templateForm.tipologia}
              onChange={(e) =>
                setTemplateForm((prev) => ({
                  ...prev,
                  tipologia: e.target.value,
                }))
              }
              options={[
                { value: "scadenza", label: "Scadenza" },
                { value: "contratto", label: "Contratto" },
                { value: "intervento", label: "Intervento" },
                { value: "manuale", label: "Manuale" },
                { value: "invito", label: "Invito" },
              ]}
            />
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={templateForm.attivo}
                  onChange={(e) =>
                    setTemplateForm((prev) => ({
                      ...prev,
                      attivo: e.target.checked,
                    }))
                  }
                  className="h-4 w-4 rounded border-gray-300 text-primary-600"
                />
                <span className="text-sm font-medium text-gray-700">
                  Template attivo
                </span>
              </label>
            </div>
          </div>

          <Input
            label="Oggetto Email *"
            value={templateForm.oggetto}
            onChange={(e) =>
              setTemplateForm((prev) => ({ ...prev, oggetto: e.target.value }))
            }
            placeholder="Es: Promemoria scadenza - {TIPOLOGIA_INTERVENTO}"
          />

          <TemplateEditor
            label="Corpo Email *"
            value={templateForm.corpo}
            onChange={(corpo) =>
              setTemplateForm((prev) => ({ ...prev, corpo }))
            }
            rows={15}
            showPreview={true}
            isHtml={templateForm.tipologia === 'invito'}
          />

          {/* Sezione allegati — visibile per tutti i template */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-700">
                Allegati PDF
              </h4>
              <span className="text-xs text-gray-500">
                {templateForm.allegati.length} selezionati
              </span>
            </div>

            {allegatiDisponibili.length === 0 ? (
              <p className="text-sm text-gray-400 italic">
                Nessun PDF disponibile nelle cartelle docs/guida-*
              </p>
            ) : (
              <div className="space-y-2">
                {/* Raggruppa per categoria */}
                {[...new Set(allegatiDisponibili.map((a) => a.categoria))].map(
                  (cat) => (
                    <div key={cat}>
                      <p className="text-xs font-medium text-gray-500 uppercase mb-1">
                        {cat}
                      </p>
                      {allegatiDisponibili
                        .filter((a) => a.categoria === cat)
                        .map((allegato) => {
                          const isSelected = templateForm.allegati.some(
                            (a) => a.file === allegato.file,
                          );
                          return (
                            <div
                              key={allegato.file}
                              className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-white transition-colors"
                            >
                              <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    setTemplateForm((prev) => ({
                                      ...prev,
                                      allegati: e.target.checked
                                        ? [
                                            ...prev.allegati,
                                            {
                                              file: allegato.file,
                                              nome: allegato.nome,
                                            },
                                          ]
                                        : prev.allegati.filter(
                                            (a) => a.file !== allegato.file,
                                          ),
                                    }));
                                  }}
                                  className="h-4 w-4 rounded border-gray-300 text-primary-600 flex-shrink-0"
                                />
                                <span className="text-sm text-gray-700 truncate">
                                  {allegato.nome}
                                </span>
                                <span className="text-xs text-gray-400 flex-shrink-0">
                                  ({Math.round(allegato.dimensione / 1024)} KB)
                                </span>
                              </label>
                              <button
                                type="button"
                                onClick={() => setPdfPreviewFile(allegato.file)}
                                className="ml-2 text-xs text-primary-600 hover:text-primary-800 hover:underline flex-shrink-0"
                              >
                                Preview
                              </button>
                            </div>
                          );
                        })}
                    </div>
                  ),
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="ghost"
              onClick={() =>
                setTemplateModal({ open: false, mode: "create", data: null })
              }
            >
              Annulla
            </Button>
            <Button variant="primary" onClick={saveTemplate}>
              {templateModal.mode === "create"
                ? "Crea Template"
                : "Salva Modifiche"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Regola */}
      <Modal
        isOpen={regolaModal.open}
        onClose={() =>
          setRegolaModal({ open: false, mode: "create", data: null })
        }
        title={
          regolaModal.mode === "create" ? "Nuova Regola" : "Modifica Regola"
        }
        size="xl"
      >
        <div className="max-h-[70vh] overflow-y-auto pr-2">
          <RegolaConfigurator
            value={regolaForm}
            onChange={setRegolaForm}
            templates={templates}
            mode={regolaModal.mode}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t mt-4">
          <Button
            variant="ghost"
            onClick={() =>
              setRegolaModal({ open: false, mode: "create", data: null })
            }
          >
            Annulla
          </Button>
          <Button
            variant="primary"
            onClick={saveRegola}
            disabled={
              !regolaForm.nome ||
              !regolaForm.evento ||
              !regolaForm.templateId ||
              !regolaForm.destinatari?.tipo
            }
          >
            {regolaModal.mode === "create" ? "Crea Regola" : "Salva Modifiche"}
          </Button>
        </div>
      </Modal>

      {/* Modal Conferma Eliminazione */}
      <Modal
        isOpen={deleteModal.open}
        onClose={() =>
          setDeleteModal({ open: false, type: null, id: null, nome: "" })
        }
        title="Conferma Eliminazione"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Sei sicuro di voler eliminare{" "}
            {deleteModal.type === "template" ? "il template" : "la regola"}{" "}
            <span className="font-semibold">"{deleteModal.nome}"</span>?
          </p>
          <p className="text-sm text-red-600">
            Questa azione non può essere annullata.
          </p>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="ghost"
              onClick={() =>
                setDeleteModal({ open: false, type: null, id: null, nome: "" })
              }
            >
              Annulla
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Elimina
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Preview PDF */}
      {pdfPreviewFile && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onClick={() => setPdfPreviewFile(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-[90vw] max-w-4xl h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
              <span className="text-sm font-medium text-gray-700 truncate">
                {pdfPreviewFile}
              </span>
              <button
                onClick={() => setPdfPreviewFile(null)}
                className="text-gray-500 hover:text-gray-800 text-xl font-bold leading-none ml-4"
              >
                ×
              </button>
            </div>
            <iframe
              src={`/api/notifiche/allegati/preview/${pdfPreviewFile}`}
              className="flex-1 w-full"
              title="Preview PDF"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default NotifichePage;
